import { useState, useCallback, useMemo } from 'react';
import type { InitiativeEntry, InitiativeState } from '../types';

/**
 * The initiative order as the server last sent it (initiative:updated / the join snapshot). The client never changes
 * the order by itself (brief 25.10, defect 1: a player's Space used to move the turn only on his screen) — every action
 * is a command, and this state follows the server's answer.
 */
export interface InitiativeView {
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  anchor?: number;
  known?: string[];
  undo?: InitiativeState['undo'];
  lastRoster?: string[];
  endedRounds?: number;
}

export interface UseInitiativeReturn extends InitiativeView {
  /** Replace the view with the server's (missing GM-only fields = none). */
  applyState: (s: Partial<InitiativeView> & { entries?: InitiativeEntry[] }) => void;
}

const EMPTY: InitiativeView = { entries: [], currentIndex: -1, round: 0 };

export function useInitiative(): UseInitiativeReturn {
  const [view, setView] = useState<InitiativeView>(EMPTY);

  const applyState = useCallback((s: Partial<InitiativeView> & { entries?: InitiativeEntry[] }) => {
    setView({
      entries: Array.isArray(s.entries) ? s.entries : [],
      currentIndex: typeof s.currentIndex === 'number' ? s.currentIndex : -1,
      round: typeof s.round === 'number' ? s.round : 0,
      ...(typeof s.anchor === 'number' ? { anchor: s.anchor } : {}),
      ...(s.known ? { known: s.known } : {}),
      ...(s.undo ? { undo: s.undo } : {}),
      ...(s.lastRoster ? { lastRoster: s.lastRoster } : {}),
      ...(typeof s.endedRounds === 'number' ? { endedRounds: s.endedRounds } : {}),
    });
  }, []);

  return useMemo(() => ({ ...view, applyState }), [view, applyState]);
}
